import type { UnitCardUnit } from '@link/share/business/UnitCard/UnitCard.types';

export interface PlanState {
  planId: string;
  selectedDate: string;
  selectedTimeId: string;
  /** Display label (slot `title`/formatted time); for display only, never compared. */
  selectedTimeTitle: string;
  /** Canonical `time` for comparisons (e.g. `isStartTimeExpired`). */
  selectedTime: string;
  selectedUnits: UnitCardUnit[];
}

export interface PlanDateFallback {
  selectedDate: string;
  displayDate: string;
}

/**
 * The plan the column layout handed the newly picked date to, and that date.
 * Pages surface it as a one-button dialog, so the user is told which plan they
 * ended up on before touching anything else.
 */
export interface PlanAutoSwitchConfirm {
  date: string;
  planTitle: string;
}

/**
 * The notice to raise when a date move lands somewhere other than the date the
 * user owns. Undefined when nothing moved, or before they own a date
 * (`selectedDate` still ''). Note this covers the move *away* from the owned
 * date only: once the replacement is owned, a later plan landing on it is
 * already what the user was told, so it moves without a second notice.
 */
export const getPlanDateFallback = (
  selectedDate: string,
  displayDate: string
): PlanDateFallback | undefined =>
  selectedDate && displayDate && selectedDate !== displayDate
    ? { selectedDate, displayDate }
    : undefined;

export const createDefaultPlanState = (planId: string): PlanState => ({
  planId,
  selectedDate: '',
  selectedTimeId: '',
  selectedTimeTitle: '',
  selectedTime: '',
  selectedUnits: []
});

export const updatePlanState = (
  prev: Map<string, PlanState>,
  planId: string,
  patch: Partial<PlanState>
): Map<string, PlanState> => {
  const current = prev.get(planId) ?? createDefaultPlanState(planId);
  return new Map(prev).set(planId, { ...current, ...patch });
};

/**
 * Drops the plan's selected time slot. The column layout clears it on every
 * plan switch, not only when the date moves: a slot belongs to one plan's
 * inventory, so carrying it to another plan would submit a slot the user was
 * never shown for it.
 */
export const clearPlanTime = (
  states: Map<string, PlanState>,
  planId: string
): Map<string, PlanState> => {
  const current = states.get(planId);
  if (!current) return states;
  if (!current.selectedTimeId && !current.selectedTimeTitle) return states;
  return new Map(states).set(planId, {
    ...current,
    selectedTimeId: '',
    selectedTimeTitle: '',
    selectedTime: ''
  });
};

export const applyPlanDateChange = (
  state: PlanState,
  selectedDate: string,
  selectedUnits: UnitCardUnit[]
): PlanState => {
  const dateChanged = state.selectedDate !== selectedDate;

  return {
    ...state,
    selectedDate,
    ...(dateChanged
      ? {
          selectedTimeId: '',
          selectedTimeTitle: '',
          selectedTime: ''
        }
      : {}),
    selectedUnits
  };
};
